import React, { useContext, useEffect, useState, useRef } from 'react';
import { Pressable, Text, TextInput, View } from 'react-native';
import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import AirbnbStyleCalendar, { formatDateRange } from "../calendar/AirbnbStyleCalendar";
import { useLoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_CONFIG } from '../../constants/GoogleMapsConfig';
import CampersBox from './CampersBox';
import LocationSuggestions, { LocationSuggestion } from './LocationSuggestions';
import SearchBoxCollaposed from './SearchBoxCollaposed';
import AddressAutocomplete from '../general/AddressAutocomplete';

interface GooglePrediction {
    description: string;
    place_id: string;
    structured_formatting: {
        main_text: string;
        secondary_text?: string;
    };
}

import { GlobalStateContext } from "../general/GlobalStateProvider";
import SearchOps from '../../data/SearchOps';
import LocationOps from '../../utility/LocationOps';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { buildExploreUrl, normalizeExploreAddress } from '../../utility/ExploreRouteUtils';

const Libraries: Array<'places'> = ['places'];

const SearchComponent = ({ atTop, zoomedIn, setZoomedIn }) => {
    const [dates, setDates] = useState([undefined, undefined]);
    const [datesSet, areDatesSet] = useState(false);

    const [gender, setGender] = useState('Any');
    const [age, setAge] = useState<number | null>(null);
    const [address, setAddress] = useState('');

    const [showCalendar, setShowCalendar] = useState(false);
    const [showCampers, setShowCampers] = useState(false);

    const [showClearAddress, setShowClearAddress] = useState(false);
    const [showClearDates, setShowClearDates] = useState(false);
    const [showClearCampers, setShowClearCampers] = useState(false);

    const calendarRef = useRef(null);
    const justClearedRef = useRef(false);
    const whereRef = useRef(null);
    const whereAreaRef = useRef(null);

    // Hover tracking
    const [hoveredElement, setHoveredElement] = useState<string | null>(null);

    const clearDates = () => {
        justClearedRef.current = true;
        setDates([undefined, undefined]);
        areDatesSet(false);
    };

    const resetAllCriteria = () => {
        setAddress('');
        setDates([undefined, undefined]);
        areDatesSet(false);
        setAge(null);
        setGender('Any');
        setShowCalendar(false);
        setShowCampers(false);
    };

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: GOOGLE_MAPS_CONFIG.googleMapsApiKey,
        libraries: Libraries,
    });

    useEffect(() => {
        if (loadError) {
            console.error('[SearchBox] ERROR: Google Maps Load Error:', loadError);
        }
        console.log('[SearchBox] DEBUG: isLoaded:', isLoaded);
    }, [isLoaded, loadError]);

    const {
        globalSessions: globalSessions,
        setGlobalSessions: setGlobalSessions,
        globalCamps: globalCamps,
        setGlobalCamps: setGlobalCamps,
        setGlobalMapCenter,
        setGlobalSearch,
        setGlobalLocation,
        globalSearch,
        globalLocation,
    } = useContext(GlobalStateContext);

    const navigate = useNavigate();
    const [isSearching, setIsSearching] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);

    // Initialize search fields from cached search data
    useEffect(() => {
        if (!hasInitialized && globalSearch) {
            if (globalSearch.address) {
                setAddress(globalSearch.address);
            }
            if (globalSearch.startDate && globalSearch.endDate) {
                setDates([globalSearch.startDate, globalSearch.endDate]);
                areDatesSet(true);
            }
            if (typeof globalSearch.age === 'number') {
                setAge(globalSearch.age);
            }
            if (globalSearch.gender) {
                setGender(globalSearch.gender);
            } else {
                setGender('Any');
            }
            setHasInitialized(true);
        }
    }, [globalSearch, hasInitialized]);

    // Handle click outside calendar
    useEffect(() => {
        const handleClickOutside = (event) => {
            const target = event.target as Node;
            // Calendar
            if (showCalendar && calendarRef.current && !calendarRef.current.contains(target)) {
                setShowCalendar(false);
            }
        };

        document.addEventListener('click', handleClickOutside, true);
        return () => {
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, [showCalendar]);



    const getCamperInfo = () => {
        const hasAge = typeof age === 'number';
        const hasGender = Boolean(gender) && gender !== 'Any';

        if (!hasAge && !hasGender) {
            return '';
        }

        if (hasAge && hasGender) {
            return `${age} year-old ${gender.toLowerCase()}`;
        }

        if (hasAge) {
            return `${age} year-old`;
        }

        return gender;
    }

    const handleSearch = async () => {
        try {
            setIsSearching(true);

            // Build search criteria from search bar inputs
            const searchCriteria: any = {};

            // Add location if provided
            if (address && address.trim() !== '') {
                searchCriteria.location = address.trim();
                searchCriteria.maxDistance = 20; // Default to 20 miles
            }

            // Add dates if selected
            if (datesSet && dates[0] && dates[1]) {
                searchCriteria.startDate = dates[0];
                searchCriteria.endDate = dates[1];
            }

            // Add age if set
            if (typeof age === 'number') {
                searchCriteria.age = age;
            }

            // Gender is optional and should only be included when explicitly selected.
            if (gender && gender !== 'Any') {
                searchCriteria.gender = gender;
            }

            // Check if any search criteria is provided
            const hasSearchCriteria = Object.keys(searchCriteria).length > 0;

            const normalizedAddress = normalizeExploreAddress(address);
            const hasAddress = normalizedAddress !== '';
            let summaryAddress: string | undefined = hasAddress ? normalizedAddress : 'Nearby';
            // When no location query: use globalLocation (physical GPS) as the search center
            if (!hasAddress) {
                // Time Square GPS coordinates.
                let usedLat = 40.758;
                let usedLng = -73.9855;

                try {
                    const current = await LocationOps.getCurrentLocation();
                    if (current?.coords?.latitude && current?.coords?.longitude) {
                        const { latitude, longitude } = current.coords;
                        usedLat = latitude;
                        usedLng = longitude;
                        setGlobalLocation && setGlobalLocation({
                            coords: { latitude, longitude },
                            timestamp: Date.now(),
                        }, true);
                    } else {
                        setGlobalMapCenter && setGlobalMapCenter({ lat: usedLat, lng: usedLng });
                    }
                } catch {
                    setGlobalMapCenter && setGlobalMapCenter({ lat: usedLat, lng: usedLng });
                }

                // Ensure search uses nearby radius around resolved center
                searchCriteria.location = `${usedLat},${usedLng}`;
                searchCriteria.maxDistance = 20;
            }

            // Persist search summary for collapsed header
            setGlobalSearch({
                address: summaryAddress,
                startDate: datesSet ? dates[0] : null,
                endDate: datesSet ? dates[1] : null,
                age: typeof age === 'number' ? age : null,
                gender: gender && gender !== 'Any' ? gender : null,
            });

            // If address provided, set global map center (parse lat,lng or geocode)
            if (searchCriteria.location) {
                try {
                    const coordMatch = searchCriteria.location.match(/^\s*(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)\s*$/);
                    if (coordMatch) {
                        const lat = parseFloat(coordMatch[1]);
                        const lng = parseFloat(coordMatch[2]);
                        if (!isNaN(lat) && !isNaN(lng)) {
                            // Only update map center, not physical location
                            setGlobalMapCenter && setGlobalMapCenter({ lat, lng });
                        }
                    } else {
                        const geocoded = await LocationOps.getLocationFromAddress(searchCriteria.location);
                        if (geocoded && geocoded.lat && geocoded.lng) {
                            // Only update map center, not physical location
                            setGlobalMapCenter && setGlobalMapCenter({ lat: geocoded.lat, lng: geocoded.lng });
                        }
                    }
                } catch { }
            }

            // Perform search (works with location, dates, age, or any combination)
            const searchResults = await SearchOps.searchCamps(searchCriteria);

            // Show appropriate success message
            if (searchCriteria.location && searchCriteria.startDate) {
                toast.success(`Found ${searchResults.length} camp${searchResults.length !== 1 ? 's' : ''} within ${searchCriteria.maxDistance} miles during selected dates`);
            } else if (searchCriteria.location) {
                toast.success(`Found ${searchResults.length} camp${searchResults.length !== 1 ? 's' : ''} within ${searchCriteria.maxDistance} miles`);
            } else if (searchCriteria.startDate) {
                toast.success(`Found ${searchResults.length} camp${searchResults.length !== 1 ? 's' : ''} with sessions during selected dates`);
            } else {
                // Default nearby search case
                toast.success(`Found ${searchResults.length} matching camp${searchResults.length !== 1 ? 's' : ''} near you`);
            }

            // Update global camps with search results
            setGlobalCamps(searchResults);

            // Also search sessions if in session view or if user wants to see sessions
            if (searchCriteria.startDate || searchCriteria.age) {
                const sessionResults = await SearchOps.searchSessions(searchCriteria);
                setGlobalSessions(sessionResults);
            }

            // Navigate to a shareable, crawlable explore URL.
            navigate(buildExploreUrl({
                address: summaryAddress,
                startDate: datesSet ? dates[0] : null,
                endDate: datesSet ? dates[1] : null,
                age: typeof age === 'number' ? age : null,
                gender: gender && gender !== 'Any' ? gender : null,
            }));

            // Collapse search bar after search
            setZoomedIn(false);

            setIsSearching(false);
        } catch (error) {
            console.error('Error performing search:', error);
            toast.error('Search failed. Please try again.');
            setIsSearching(false);
        }
    };

    const styles = {
        container: {
            alignItems: 'center' as const,
            marginTop: -20,
            marginBottom: 20,
            marginLeft: 0,
        },
        searchBoxContainer: {
            flexWrap: 'wrap' as const,
            flexDirection: 'row' as const,
            alignItems: 'center' as const,
            width: 900,
            backgroundColor: 'white',
            borderWidth: 1,
            borderRadius: 80,
            paddingHorizontal: 28,
            paddingVertical: 10,
            borderColor: '#E6E6E6',
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.08,
            shadowRadius: 12,
            elevation: 5,
        },
        inputContainer: {
            flex: 1,
            marginRight: 15,
        },
        whereContainer: {
            flex: 1.6,
            marginRight: 15,
        },
        input: {
            height: 42,
            borderRadius: 50,
            paddingHorizontal: 14,
            fontSize: 15,
            flexDirection: 'row' as const,
            alignItems: 'center' as const,
            justifyContent: 'space-between' as const,
            backgroundColor: '#F8F9FA',
            borderWidth: 1,
            borderColor: '#E6E6E6',
        },
        hoveredInput: {
            backgroundColor: '#eef2f5',
            borderColor: '#cbd5e1',
        },
        label: {
            paddingLeft: 8,
            marginBottom: 6,
            fontSize: 17,
            fontWeight: '600' as const,
        },
        button: {
            flexDirection: 'row' as const,
            backgroundColor: 'black',
            paddingVertical: 10,
            paddingHorizontal: 16,
            borderRadius: 50,
            marginTop: 20,
            marginLeft: 10,
        },
        buttonText: {
            color: 'white',
            fontSize: 16,
            fontWeight: '400' as const,
            marginLeft: 10,
        },
        clearButton: {
            position: 'absolute' as const,
            bottom: 10,
            right: 10,
            backgroundColor: 'lightgrey',
            borderRadius: 10,
            width: 20,
            height: 20,
            alignItems: 'center' as const,
            justifyContent: 'center' as const,
            zIndex: 1100,
        },
        dualButton: {
            flexDirection: 'row' as const,
            alignItems: 'center' as const,
            borderRadius: 50,
            marginTop: 20,
            marginLeft: 10,
            overflow: 'hidden' as const,
        },
        resetButtonSide: {
            flexDirection: 'row' as const,
            alignItems: 'center' as const,
            justifyContent: 'center' as const,
            backgroundColor: '#F8F9FA',
            height: 42,
            paddingHorizontal: 16,
            borderTopLeftRadius: 50,
            borderBottomLeftRadius: 50,
        },
        resetSideText: {
            color: '#666',
            fontSize: 14,
            fontWeight: '500' as const,
            marginLeft: 6,
        },
        hoveredReset: {
            backgroundColor: '#eceff1',
        },
        searchButtonSide: {
            flexDirection: 'row' as const,
            alignItems: 'center' as const,
            justifyContent: 'center' as const,
            backgroundColor: '#3C8248',
            height: 42,
            paddingHorizontal: 18,
            borderTopRightRadius: 50,
            borderBottomRightRadius: 50,
        },
        hoveredSearch: {
            backgroundColor: '#2f6a3a',
        },
        searchSideText: {
            color: 'white',
            fontSize: 15,
            fontWeight: '600' as const,
            marginLeft: 6,
        }
    };

    return (
        (atTop || zoomedIn)
            ? <View style={styles.container}>
                <View style={styles.searchBoxContainer}>
                    <View style={styles.whereContainer}>
                        <Text style={styles.label}>Where</Text>
                        <View ref={whereAreaRef} style={{ flex: 1, position: 'relative' as const }}>
                            <AddressAutocomplete
                                value={address}
                                onChangeText={setAddress}
                                onSelectPlace={(id, desc) => {
                                    setAddress(desc);
                                }}
                                placeholder="Nearby"
                                showNearbyOption
                                containerStyle={{ zIndex: 1200 }}
                                inputStyle={[
                                    styles.input,
                                    hoveredElement === 'where' && styles.hoveredInput,
                                    { height: 42, borderRadius: 50, borderWidth: 1, borderColor: '#E6E6E6' }
                                ]}
                            />
                        </View>
                    </View>
                    <View style={styles.inputContainer}>
                        <Text style={styles.label}>When</Text>
                        <View style={{ flexDirection: 'row' }}>
                            <Pressable
                                onHoverIn={() => setHoveredElement('when')}
                                onHoverOut={() => setHoveredElement(null)}
                                style={[styles.input, hoveredElement === 'when' && styles.hoveredInput, { flex: 1 }]}
                                onPress={() => {
                                    setShowCalendar(true); // Always show calendar when clicked
                                }}
                            >
                                <Text style={{ color: datesSet ? 'black' : 'grey', fontSize: 15 }}>
                                    {datesSet ? formatDateRange(dates[0], dates[1]) : 'Next 30 days'}
                                </Text>
                                <MaterialCommunityIcons name={'calendar-blank-outline'} size={18} color={'#666'} />
                            </Pressable>
                        </View>
                    </View>
                    <View style={styles.inputContainer}>
                        <Text style={styles.label}>Who</Text>
                        <View style={{ flexDirection: 'row' }}>
                            <Pressable
                                onHoverIn={() => setHoveredElement('who')}
                                onHoverOut={() => setHoveredElement(null)}
                                onPress={() => setShowCampers(true)}
                                style={[styles.input, hoveredElement === 'who' && styles.hoveredInput, { flex: 1 }]}
                            >
                                <Text
                                    style={{
                                        flex: 1,
                                        fontSize: 15,
                                        color: getCamperInfo() ? 'black' : 'grey',
                                    }}
                                >
                                    {getCamperInfo() || 'Add attendee'}
                                </Text>
                                <MaterialCommunityIcons name={'account-outline'} size={18} color={'#666'} />
                            </Pressable>
                        </View>
                    </View>

                    <View style={styles.dualButton}>
                        <Pressable
                            onHoverIn={() => setHoveredElement('reset')}
                            onHoverOut={() => setHoveredElement(null)}
                            style={[styles.resetButtonSide, hoveredElement === 'reset' && styles.hoveredReset]}
                            onPress={resetAllCriteria}
                        >
                            <MaterialCommunityIcons name={'refresh'} size={16} color={'#666'} />
                            <Text style={styles.resetSideText}>Reset</Text>
                        </Pressable>
                        <Pressable
                            onHoverIn={() => setHoveredElement('search')}
                            onHoverOut={() => setHoveredElement(null)}
                            style={[
                                styles.searchButtonSide,
                                hoveredElement === 'search' && styles.hoveredSearch,
                                isSearching && { opacity: 0.7 }
                            ]}
                            onPress={handleSearch}
                            disabled={isSearching}
                        >
                            {isSearching ? (
                                <>
                                    <MaterialCommunityIcons name={'loading'} size={18} color={'white'} />
                                    <Text style={styles.searchSideText}>Searching...</Text>
                                </>
                            ) : (
                                <>
                                    <MaterialCommunityIcons name={'magnify'} size={18} color={'white'} />
                                    <Text style={styles.searchSideText}>Search</Text>
                                </>
                            )}
                        </Pressable>
                    </View>
                </View>

                {showCalendar && (
                    <View
                        ref={calendarRef}
                        style={{
                            position: 'absolute',
                            top: 80,
                            left: '50%',
                            marginLeft: -350, // Half of maxWidth (700/2)
                            zIndex: 1000,
                            backgroundColor: 'white',
                            borderRadius: 12,
                            shadowColor: '#000',
                            shadowOffset: { width: 0, height: 4 },
                            shadowOpacity: 0.25,
                            shadowRadius: 12,
                            elevation: 8,
                            width: 700,
                            maxWidth: 700,
                        }}>
                        {/* Title row with close button */}
                        <View style={{
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            paddingHorizontal: 24,
                            paddingTop: 20,
                            paddingBottom: 8,
                            borderBottomWidth: 1,
                            borderBottomColor: '#f0f0f0',
                        }}>
                            <Text style={{
                                fontSize: 18,
                                fontWeight: '600',
                                color: '#222',
                            }}>
                                Select dates
                            </Text>
                            <Pressable
                                style={{
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: 6,
                                    paddingHorizontal: 12,
                                    paddingVertical: 8,
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 1100,
                                }}
                                onPress={() => {
                                    clearDates();
                                }}
                            >
                                <Text style={{
                                    fontSize: 14,
                                    fontWeight: '500',
                                    color: '#666'
                                }}>
                                    Clear dates
                                </Text>
                            </Pressable>
                        </View>

                        <AirbnbStyleCalendar
                            startDate={datesSet ? dates[0] : undefined}
                            endDate={datesSet ? dates[1] : undefined}
                            onDateChange={() => {
                                // Don't accept dates when we just cleared them
                                if (justClearedRef.current) {
                                    justClearedRef.current = false; // Reset flag after ignoring
                                }
                            }}
                            onSelectionComplete={(startDate, endDate) => {
                                // Only update if different from current selection
                                const isDifferentFromCurrent = !datesSet ||
                                    dates[0]?.getTime() !== startDate.getTime() ||
                                    dates[1]?.getTime() !== endDate.getTime();

                                if (isDifferentFromCurrent) {
                                    setDates([startDate, endDate]);
                                    areDatesSet(true);
                                    setShowCalendar(false);
                                }
                            }}
                            minDate={new Date()}
                        />
                    </View>
                )}

                {showCampers && <CampersBox age={age} setAge={setAge} gender={gender} setGender={setGender}
                    setShowCampers={setShowCampers} />}
            </View>
            : <SearchBoxCollaposed
                whereLabel={address === '' ? 'Anywhere' : address}
                whenLabel={!datesSet ? 'Anytime' : formatDateRange(dates[0], dates[1])}
                whoLabel={getCamperInfo() || 'Anyone'}
                onExpand={() => setZoomedIn(true)}
            />
    );
};

export default SearchComponent;
